import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { changePassword, getCurrentUser } from '../services/authService';
import Header from '../components/Header';

export default function ChangePasswordPage() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // Check if user is Google OAuth user
  useEffect(() => {
    const checkAuthMethod = async () => {
      try {
        const userProfile = await getCurrentUser();
        if (userProfile.auth_provider === 'google') {
          setIsGoogleUser(true);
        }
      } catch (err) {
        console.error('Failed to check auth method:', err);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuthMethod();
  }, []);

  const validateCurrentPassword = (value: string): boolean => {
    if (!value) {
      setCurrentPasswordError(t('security.current_password_required'));
      return false;
    }
    setCurrentPasswordError('');
    return true;
  };

  const validateNewPassword = (value: string): boolean => {
    if (!value) {
      setNewPasswordError(t('security.new_password_required'));
      return false;
    }
    if (value.length < 8) {
      setNewPasswordError(t('security.new_password_min'));
      return false;
    }
    if (value === currentPassword) {
      setNewPasswordError(t('security.new_password_same'));
      return false;
    }
    setNewPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string): boolean => {
    if (!value) {
      setConfirmPasswordError(t('security.confirm_password_required'));
      return false;
    }
    if (value !== newPassword) {
      setConfirmPasswordError(t('security.confirm_password_mismatch'));
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleCurrentPasswordChange = (value: string) => {
    setCurrentPassword(value);
    if (currentPasswordError) validateCurrentPassword(value);
    // Re-validate new password if it exists (to check if same as current)
    if (newPassword && newPasswordError) validateNewPassword(newPassword);
  };

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    if (newPasswordError) validateNewPassword(value);
    // Re-validate confirm password if it exists
    if (confirmPassword && confirmPasswordError) validateConfirmPassword(confirmPassword);
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (confirmPasswordError) validateConfirmPassword(value);
  };

  const handleSave = async () => {
    setError('');
    setSuccess(false);

    // Validate all fields
    const isCurrentValid = validateCurrentPassword(currentPassword);
    const isNewValid = validateNewPassword(newPassword);
    const isConfirmValid = validateConfirmPassword(confirmPassword);

    if (!isCurrentValid || !isNewValid || !isConfirmValid) {
      return;
    }

    setLoading(true);

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      setSuccess(true);
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Password change invalidates all sessions for security
      // Sign out and redirect to login
      setTimeout(async () => {
        try {
          await signOut();
        } catch (error) {
          console.error('Sign out error:', error);
        }
        // Navigate to login with a message
        navigate('/login', { 
          state: { 
            message: t('security.login_message') 
          } 
        });
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('security.error_default'));
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  const isFormValid = currentPassword && newPassword && confirmPassword && 
                      !currentPasswordError && !newPasswordError && !confirmPasswordError;

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#ffffeb] flex items-center justify-center">
        <div
          className="w-16 h-16 border-4 border-[#077f5d] border-t-transparent"
          style={{
            animation: 'spin 1s linear infinite'
          }}
        ></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#ffffeb]"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Header */}
      <Header
        leftContent={
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="lg:hidden p-2 hover:bg-[#2da877] border-2 border-[#077f5d]"
              style={{
                boxShadow: '2px 2px 0 #077f5d',
                transition: 'none'
              }}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <p
              className="text-left text-base font-bold uppercase"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '14px',
              }}
            >
              {t('security.title')}
            </p>
          </div>
        }
        maxWidth="max-w-lg"
        hideProfileMenu
      />

      {/* Content */}
      <div className="max-w-lg mx-auto px-6 py-6 pb-24 lg:pb-8">
        {/* Google User Error */}
        {isGoogleUser && (
          <div
            className="mb-4 bg-yellow-50 border-2 border-yellow-600 text-yellow-800 px-4 py-3 font-mono"
            style={{ boxShadow: '3px 3px 0 #ca8a04' }}
          >
            <p className="text-sm font-bold">⚠️ {t('security.google_warning')}</p>
            <button
              onClick={handleCancel}
              className="mt-3 text-sm font-bold text-yellow-900 hover:text-yellow-700 underline"
              style={{ transition: 'none' }}
            >
              {t('security.back_to_profile')}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && !isGoogleUser && (
          <div
            className="mb-4 bg-red-50 border-2 border-red-600 text-red-700 px-4 py-3 font-mono"
            style={{ boxShadow: '3px 3px 0 #dc2626' }}
          >
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div
            className="mb-4 bg-[#dffbd0] border-2 border-[#077f5d] text-[#077f5d] px-4 py-3 font-mono"
            style={{ boxShadow: '3px 3px 0 #077f5d' }}
          >
            <p className="text-sm font-bold">✓ {t('security.success_message')}</p>
            <p className="text-xs mt-1">{t('security.success_redirect')}</p>
          </div>
        )}

        {/* Form - Only show if not Google user */}
        {!isGoogleUser && (
          <>
            <div className="space-y-4">
              {/* Current Password Field */}
              <div className="bg-white border-2 border-[#077f5d] p-4" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
                <label
                  className="block text-[#077f5d] mb-2 uppercase"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700
                  }}
                >
                  {t('security.current_password')}
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => handleCurrentPasswordChange(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 border-2 outline-none font-mono ${
                      currentPasswordError
                        ? 'border-red-600 bg-red-50'
                        : 'border-[#077f5d] bg-white'
                    }`}
                    style={{
                      boxShadow: currentPasswordError ? '2px 2px 0 #dc2626' : '2px 2px 0 #077f5d',
                      transition: 'none'
                    }}
                    placeholder={t('security.current_password_placeholder')}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#077f5d] hover:text-[#2da877]"
                    style={{ transition: 'none' }}
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {currentPasswordError && (
                  <p className="mt-2 text-sm text-red-600 font-mono">{currentPasswordError}</p>
                )}
              </div>

              {/* New Password Field */}
              <div className="bg-white border-2 border-[#077f5d] p-4" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
                <label
                  className="block text-[#077f5d] mb-2 uppercase"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700
                  }}
                >
                  {t('security.new_password')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => handleNewPasswordChange(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 border-2 outline-none font-mono ${
                      newPasswordError
                        ? 'border-red-600 bg-red-50'
                        : 'border-[#077f5d] bg-white'
                    }`}
                    style={{
                      boxShadow: newPasswordError ? '2px 2px 0 #dc2626' : '2px 2px 0 #077f5d',
                      transition: 'none'
                    }}
                    placeholder={t('security.new_password_placeholder')}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#077f5d] hover:text-[#2da877]"
                    style={{ transition: 'none' }}
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {newPasswordError && (
                  <p className="mt-2 text-sm text-red-600 font-mono">{newPasswordError}</p>
                )}
                <p className="mt-2 text-xs text-[#077f5d] font-mono">{t('security.password_hint')}</p>
              </div>

              {/* Confirm Password Field */}
              <div className="bg-white border-2 border-[#077f5d] p-4" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
                <label
                  className="block text-[#077f5d] mb-2 uppercase"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700
                  }}
                >
                  {t('security.confirm_password')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    className={`w-full px-4 py-3 pr-12 border-2 outline-none font-mono ${
                      confirmPasswordError
                        ? 'border-red-600 bg-red-50'
                        : 'border-[#077f5d] bg-white'
                    }`}
                    style={{
                      boxShadow: confirmPasswordError ? '2px 2px 0 #dc2626' : '2px 2px 0 #077f5d',
                      transition: 'none'
                    }}
                    placeholder={t('security.confirm_password_placeholder')}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#077f5d] hover:text-[#2da877]"
                    style={{ transition: 'none' }}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="mt-2 text-sm text-red-600 font-mono">{confirmPasswordError}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <button
                onClick={handleSave}
                disabled={loading || !isFormValid}
                className="w-full bg-[#3cbe8c] text-white font-bold py-3 px-6 border-2 border-[#077f5d] hover:bg-[#2da877] disabled:opacity-50 disabled:cursor-not-allowed uppercase flex items-center justify-center gap-2"
                style={{
                  boxShadow: '4px 4px 0 #077f5d',
                  transition: 'none',
                  fontFamily: "'IBM Plex Mono', monospace"
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('security.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {t('security.save')}
                  </>
                )}
              </button>

              <button
                onClick={handleCancel}
                disabled={loading}
                className="w-full bg-white text-[#1a1a1a] font-bold py-3 px-6 border-2 border-[#077f5d] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  boxShadow: '4px 4px 0 #077f5d',
                  transition: 'none',
                  fontFamily: "'IBM Plex Mono', monospace"
                }}
              >
                {t('security.cancel')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
